import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SessionService } from '../services/session.service';
import { LoadingPanelService } from '../services/LoadingPanel/loading-panel.service';
import { Endpoint } from '../custom-classes/Endpoint';
import { EndpointService } from '../services/endpoint.service';
import { ConfirmDialogService } from '../services/confirm-dialog.service';
import { CustomErrorHandler } from '../custom-classes/CustomErrorHandler';
import { projectConfiguration } from '../app.config';

/** 
 * Intercepts the http calls.
 * 
 * `Loading panel` : Shows at the start of the request and hides it at the end.
 * `Authorization header` : Included automatically if the endpoint requires it.
 * 
 * `Errors` : If the call returns an error :
 * - DEV AND PRE : Show the full stacktrace on a dialog.
 * - PRO : If the error has a message, only show the message. Otherwise, show a  generic error.
 *  
 */

@Injectable()
export class HttpListenerInterceptor implements HttpInterceptor {

  session = inject(SessionService);
  router = inject(Router);
  loadingPanel = inject(LoadingPanelService);
  endpointS = inject(EndpointService);
  confirmDialog = inject(ConfirmDialogService);
  errorHandler = inject(CustomErrorHandler);

  // Añade un Set para trackear llamadas en curso
  private pendingRequests = new Set<string>();

  isDeployInfoEndpoint(req: HttpRequest<any>) {
    return req.url.includes('/assets/deploy-info.json');
  }

  /** Don't show loading if the route is assets/icons/something.svg
   * Example :http://localhost:4200/assets/icons/foo.svg
  */
  isSvgEndpoint(req: HttpRequest<any>) {
      let isSvg = req.url.includes("/assets/icons/") && req.url.includes(".svg");
      let isFlag = req.url.includes("assets/svg-country-flags/svg") && req.url.includes(".svg");
      return isSvg || isFlag;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> 
  {
    // IGNORAR SENTRY COMPLETAMENTE
    if (req.url.includes('sentry.io') || req.url.includes('ingest.sentry.io')) {
        console.log("🚫 Ignorando petición a Sentry - pasando sin modificar");
        return next.handle(req);
    }

    if (this.isDeployInfoEndpoint(req)) {
        return next.handle(req);
    }

    console.log("Intercepting!");

    let endp: Endpoint | undefined = this.endpointS.getEnpointFromUrl(req.url);
    const requestKey = `${req.method}_${req.url}`;
    
    if ((endp && endp.shouldShowLoading()) || (endp == undefined && !this.isSvgEndpoint(req))) {
        console.log(endp != undefined ? "Interceptado el endpoint : " + endp.url : "El endpoint interceptado no se reconoce");
        if (!this.pendingRequests.has(requestKey)) {
            console.log("Mostrando loading para:", req.url);
            this.loadingPanel.show();
            this.pendingRequests.add(requestKey);
        }
    }

    let authReq: HttpRequest<any>;
    let finalreq: HttpRequest<any>;
    //Miramos si es necessario añadir el header de autorización
    if ((endp != undefined && endp.requiresAuthentication())) {
        authReq = req.clone({
            setHeaders: {
                Authorization: 'Bearer ' + this.session.getToken()
            }
        });
        // console.log("Añadiendo el header de autorización al endpoint : " + endp.url)
    }
    else {  //En caso de que no sea necesario el header de autorización 
        authReq = req.clone();
    }
    
    if (this.session.isSharedAccessView) {
        req = this.addSharedUser(authReq);
        authReq = this.addSharedUser(authReq);
    }

    finalreq = authReq.clone({});

    return next.handle(finalreq)
      .pipe(finalize(() => {
            // Ocultar loading y limpiar pending requests
            if (this.pendingRequests.has(requestKey)) {
                this.pendingRequests.delete(requestKey);
                this.loadingPanel.hide();
            }
        }))
      .pipe(
        /**Gestión de errores de las peticiones http */
        catchError((error: HttpErrorResponse) => {
            /** Mostramos el loading panel */
            this.pendingRequests.delete(requestKey);
            this.loadingPanel.hide();
            console.log(error);
            const noAuth = this.isAuthError(error);
            /** Show the panel only if the endpoint is undefined or the endpoint has the customError to false */
            if (endp == undefined || endp.customError == false) {
                this.handleError(
                  error, 
                  endp, 
                  this.getErrorCode(error), 
                  noAuth, 
                  this.isTimeOutError(error)
                );
            }
            /** Reportamos y loggeamos errores */
            if (!noAuth && this.session.hasSession()) {
                // if (feature.reportLogs) { this.errorHandler.report(new CustomLog("error", JSON.stringify(error))) }
                // if (feature.toJira) { this.errorHandler.toJira(document.title, window.location.href, new Date().shortSecondsFormat(), finalreq, error); }
            }
            /** Escondemos loading panel y lanzamos error con dialogo */
            this.loadingPanel.hide(true);
            return throwError(() => new Error(this.confirmDialog.getMessageError(error)));
          }
        )
      );
  }

  /** Añade el id del usuario por el cual se ha intercambiado la sesión para que el backend tenga conocimiento */
  private addSharedUser(req: HttpRequest<any>) {
      let sharedReq: HttpRequest<any> = req.clone();
      const currentUser = this.session.sharedUserId;

      if (!currentUser) { return req; }

      if (sharedReq.body instanceof FormData) {
          sharedReq.body.append("shared_user", currentUser.toString());
      }
      else if (typeof sharedReq.body === 'object' && sharedReq.body !== null) {
          const modifiedBody = {
              ...sharedReq.body,
              shared_user: currentUser
          };
          sharedReq = sharedReq.clone({ body: modifiedBody });
      }
      return sharedReq;
  }

  /**
   * Funcion que se encarga de gestionar los errores HTTP. Si quisieramos añadir la gestion de un error concreto habriamos de añadirla solo aqui.
   * @param error 
   * @param endp 
   * @param coreErrorCodes 
   * @param isAuthError 
   * @param isTimeoutError 
   */
  private handleError(
    error: HttpErrorResponse, 
    endp: any, 
    coreErrorCodes: any, 
    isAuthError: boolean, 
    isTimeoutError: boolean) : void
  {
    /** NO SESSION */
    if (isAuthError) {
        this.handleAuthError(endp);
    } else if (coreErrorCodes != undefined) {
        /** ERROR CODES */
        this.handleCoreError(coreErrorCodes, endp);
    } else if (isTimeoutError) {
        /**TIMEOUT */
        this.handleTimeoutError();
    } else {
        /** DEFAULT ERROR HANDLING */
        this.handleGenericHttpError(error);
    }
  }

  /**
   * Gestiona errores de autenticación.
   * @param endp 
   */
  private handleAuthError(endp: any) : void
  {
    const shouldRedirect = endp == undefined || endp.requiresAuthentication();
    if (shouldRedirect) {
        this.session.clearEinaAndGoLanding(true);
    }
  }

  private handleForbiddenError(endp: any) : void
  {
    // Gestion del error de permisos
    this.confirmDialog.showError("¡Parece que no tienes permisos para realizar esa acción!");
  }

  /**
   * Gestiona errores de nuestra logica de aplicacion
   * @param coreErrorCodes 
   * @param endp 
   */
  private handleCoreError(coreErrorCodes : any, endp: any) : void
  {
    /** We KNOW what error is, this is becaouse we put 'Ha surgido un error' */
    this.confirmDialog.showError("Ha surgido un error", coreErrorCodes.errorMessage);
    if (coreErrorCodes?.errorCode == 1) {
        const shouldRedirect = endp == undefined || endp.requiresAuthentication();
        if (shouldRedirect) {
            this.session.clearEinaAndGoLanding(true);
        }
    }
  }

  /**
   * Gestiona los errores por timeout
   */
  private handleTimeoutError() : void
  {
    this.confirmDialog.showError("Alcanzado tiempo de espera máximo", "Revise su conexión a internet");
  }

  /**
   * Gestiona errores genericos (todo el resto de codigos http)
   * @param error 
   */
  private handleGenericHttpError(error: HttpErrorResponse) : void
  {
    /** Show genereic HTTP error */
    this.confirmDialog.showGenericHttpError(error);
  }


  private getErrorCode(resp: HttpErrorResponse): { errorCode: number, errorMessage: string } | undefined {
      var error_code = resp.error.error_code;
      if (projectConfiguration.errorCodes) {
          let error_message = projectConfiguration.errorCodes.get(error_code);
          if (typeof error_code == "number" && error_message) {
              return { errorCode: error_code, errorMessage: error_message };
          }
      }
      return undefined;
  }

  private isAuthError(error: HttpErrorResponse) 
  {
      return error.status === 401;
      // Trabajar con cadenas de texto es pericolosooooo, trabajemos mejor con estados http.
      //return error.error.message === this.session.NO_SESSION;
  }

  private isTimeOutError(error: HttpErrorResponse) 
  {
      return error.status === 408;
      // mismo argumento, es pericoloso trabajar con strings hardcoded.
      return error.error.type && error.error.type == "timeout";
  }


}

